/**
 * Cart hook - auth-aware with Firebase sync for Save for Later
 */
import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from './useTypedRedux';
import {
  addToCart,
  removeFromCart,
  updateQuantity,
  incrementQuantity,
  decrementQuantity,
  saveForLater,
  moveToCart,
  removeSavedItem as removeSavedItemAction,
  clearCart,
  clearSavedItems,
  openCartDrawer,
  closeCartDrawer,
  applyCoupon as applyCouponAction,
  removeCoupon as removeCouponAction,
  getCartItemId,
  selectCartItems,
  selectSavedItems,
  selectCartItemCount,
  selectCartSummary,
  selectIsCartDrawerOpen,
  selectAppliedCoupon,
} from '@/store/slices/cartSlice';
import { selectIsAuthenticated, selectUser } from '@/store/slices/authSlice';
import { openLoginPrompt } from '@/store/slices/uiSlice';
import {
  addToSavedForLaterFB,
  removeFromSavedForLaterFB,
  clearSavedForLaterFB,
} from '@/services/firebase/savedForLater.service';
import { Product } from '@/types';
import type { AppliedCoupon } from '@/types';

const buildId = (productId: string, selectedSize?: string): string =>
  selectedSize ? `${productId}::${selectedSize}` : productId;

/**
 * Hook for cart operations with auth-aware Save for Later
 */
export const useCart = () => {
  const dispatch = useAppDispatch();
  
  const items = useAppSelector(selectCartItems);
  const savedItems = useAppSelector(selectSavedItems);
  const itemCount = useAppSelector(selectCartItemCount);
  const summary = useAppSelector(selectCartSummary);
  const isDrawerOpen = useAppSelector(selectIsCartDrawerOpen);
  const appliedCoupon = useAppSelector(selectAppliedCoupon);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const user = useAppSelector(selectUser);

  const addItem = useCallback(
    (product: Product, quantity: number = 1, selectedSize?: string) => {
      dispatch(addToCart({ product, quantity, selectedSize }));
    },
    [dispatch]
  );

  const addMultipleItems = useCallback(
    (entries: { product: Product; quantity: number; selectedSize?: string }[]) => {
      entries.forEach(({ product, quantity, selectedSize }) => {
        if (quantity > 0) {
          dispatch(addToCart({ product, quantity, selectedSize }));
        }
      });
    },
    [dispatch]
  );

  const removeItem = useCallback(
    (productId: string, selectedSize?: string) => {
      dispatch(removeFromCart(buildId(productId, selectedSize)));
    },
    [dispatch]
  );

  const setQuantity = useCallback(
    (productId: string, quantity: number, selectedSize?: string) => {
      dispatch(updateQuantity({ productId: buildId(productId, selectedSize), quantity }));
    },
    [dispatch]
  );

  const increment = useCallback(
    (productId: string, selectedSize?: string) => {
      dispatch(incrementQuantity(buildId(productId, selectedSize)));
    },
    [dispatch]
  );

  const decrement = useCallback(
    (productId: string, selectedSize?: string) => {
      dispatch(decrementQuantity(buildId(productId, selectedSize)));
    },
    [dispatch]
  );

  const saveItemForLater = useCallback(
    (productId: string, selectedSize?: string) => {
      if (!isAuthenticated || !user) {
        dispatch(openLoginPrompt());
        return;
      }
      const compositeId = buildId(productId, selectedSize);
      dispatch(saveForLater(compositeId));
      addToSavedForLaterFB(user.id, compositeId);
    },
    [dispatch, isAuthenticated, user]
  );

  const moveItemToCart = useCallback(
    (productId: string, selectedSize?: string) => {
      const compositeId = buildId(productId, selectedSize);
      dispatch(moveToCart(compositeId));
      if (isAuthenticated && user) {
        removeFromSavedForLaterFB(user.id, compositeId);
      }
    },
    [dispatch, isAuthenticated, user]
  );

  const removeSavedItem = useCallback(
    (productId: string, selectedSize?: string) => {
      const compositeId = buildId(productId, selectedSize);
      dispatch(removeSavedItemAction(compositeId));
      if (isAuthenticated && user) {
        removeFromSavedForLaterFB(user.id, compositeId);
      }
    },
    [dispatch, isAuthenticated, user]
  );

  const clear = useCallback(() => {
    dispatch(clearCart());
  }, [dispatch]);

  const clearSaved = useCallback(() => {
    dispatch(clearSavedItems());
    if (isAuthenticated && user) {
      clearSavedForLaterFB(user.id);
    }
  }, [dispatch, isAuthenticated, user]);

  const applyCoupon = useCallback(
    (coupon: AppliedCoupon) => {
      dispatch(applyCouponAction(coupon));
    },
    [dispatch]
  );

  const removeCoupon = useCallback(() => {
    dispatch(removeCouponAction());
  }, [dispatch]);

  const openDrawer = useCallback(() => {
    dispatch(openCartDrawer());
  }, [dispatch]);

  const closeDrawer = useCallback(() => {
    dispatch(closeCartDrawer());
  }, [dispatch]);

  const isInCart = useCallback(
    (productId: string, selectedSize?: string) => {
      const id = buildId(productId, selectedSize);
      return items.some((item) => getCartItemId(item) === id);
    },
    [items]
  );

  const getItemQuantity = useCallback(
    (productId: string, selectedSize?: string) => {
      const id = buildId(productId, selectedSize);
      const item = items.find((item) => getCartItemId(item) === id);
      return item?.quantity ?? 0;
    },
    [items]
  );

  return {
    // State
    items,
    savedItems,
    itemCount,
    summary,
    isDrawerOpen,
    isEmpty: items.length === 0,
    appliedCoupon,
    
    // Actions
    addItem,
    addMultipleItems,
    removeItem,
    setQuantity,
    increment,
    decrement,
    saveItemForLater,
    moveItemToCart,
    removeSavedItem,
    clear,
    clearSaved,
    applyCoupon,
    removeCoupon,
    openDrawer,
    closeDrawer,
    
    // Helpers
    isInCart,
    getItemQuantity,
    getCartItemId,
  };
};

export default useCart;
